<template>
  <div>
    <Breadcrumbs v-if="!isHome">
      <span>{{ this.getNavName('NAV_TICKETS') }}</span>
    </Breadcrumbs>
    <div class="eventsPage">
      <Loading :visible="loading" type="fullPage" size="large" />
      <div v-if="!loading && loaded && Object.keys(events).length === 0" class="noItems">
        <h2>{{ t('TICKETS_NO_EVENTS') }}</h2>
      </div>
      <div v-else class="events">
        <div class="filters">
          <div class="venues" v-if="venues.length > 1">
            <transition-group name="fade">
              <CheckInput
                type="button"
                class="venue"
                v-for="(venue, key) in venues"
                :key="venue.name"
                :fname="key"
                :model-value="venue.selected"
                @update:model-value="
                  (v) =>
                    setVenueFilterLocal({
                      key: key,
                      selected: v,
                    })
                "
                :label="venue.name"
              ></CheckInput>
            </transition-group>
          </div>
          <div class="groups" v-if="groups.length > 0">
            <transition-group name="fade">
              <CheckInput
                type="button"
                class="venue"
                v-for="(group, key) in groups"
                :key="group.name"
                :fname="key"
                :model-value="group.selected"
                @update:model-value="
                  (v) =>
                    setGroupFilterLocal({
                      key: key,
                      selected: v,
                    })
                "
                :label="group.name"
              ></CheckInput>
            </transition-group>
          </div>
        </div>
        <div v-if="Object.keys(filteredEvents).length === 0 && !loading && loaded" class="noItems">
          <h2>{{ $t('TICKETS_NO_EVENTS_MATCHING_FILTERS') }}</h2>
        </div>
        <div class="eventContainer">
          <transition-group name="fade">
            <template v-if="showEvents">
              <div class="event" :key="eventId" v-for="eventId in filteredEventOrder">
                <router-link class="linkBlock" :to="{ name: 'ticket', params: { id: eventId } }">
                  <div class="imageContainer">
                    <ImageC :srcSuffix="filteredEvents[eventId].event_image" @load="imageLoaded" />
                  </div>
                  <div class="textContainer">
                    <div class="title">{{ filteredEvents[eventId].name }}</div>
                    <div
                      class="calendar"
                      v-if="
                        formatStartAndEndDates(
                          filteredEvents[eventId].start,
                          filteredEvents[eventId].end
                        ) !== ''
                      "
                    >
                      <Icon icon="calendar"></Icon>
                      {{
                        formatStartAndEndDates(
                          filteredEvents[eventId].start,
                          filteredEvents[eventId].end
                        )
                      }}
                    </div>
                    <div class="venue" v-if="hasMultipleVenues">
                      <icon class="icon" icon="museum" />
                      <span class="venueName">{{ filteredEvents[eventId].venue }}</span>
                    </div>
                  </div>
                </router-link>
              </div>
            </template>
          </transition-group>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import moment from 'moment-mini';
import Loading from '../Loading.vue';
import Breadcrumbs from '../Breadcrumbs.vue';
import formater from '../../lib/formater';
import Icon from '../Icon.vue';
import CheckInput from '../Form/CheckInput.vue';
import ImageC from '../ImageC.vue';
import { setPageTitle } from '../../lib/pageTitle';

export default {
  name: 'TicketList',
  components: {
    Loading,
    Breadcrumbs,
    Icon,
    CheckInput,
    ImageC,
  },
  data() {
    return {
      showEvents: false,
      maxImageHeight: 0,
    };
  },
  metaInfo() {
    let title = this.$t('TICKETS_PAGE_TITLE');
    let description = '';
    let keywords = '';
    let image = '';
    if (typeof this.meta !== 'undefined') {
      if (typeof this.meta.ticket !== 'undefined') {
        if (typeof this.meta.ticket.title !== 'undefined') {
          title = this.meta.ticket.title;
        }
        if (typeof this.meta.ticket.description !== 'undefined') {
          description = this.meta.ticket.description;
        }
        if (typeof this.meta.ticket.keywords !== 'undefined') {
          keywords = this.meta.ticket.keywords;
        }
        if (typeof this.meta.ticket.image !== 'undefined' && this.meta.ticket.image !== '') {
          image = `${this.publicURL}1200/${this.meta.ticket.image}`;
        }
      }
    }
    return {
      meta: [
        {
          vmid: 'description',
          name: 'description',
          content: description,
        },
        {
          vmid: 'title',
          name: 'title',
          content: title,
        },
        {
          vmid: 'keywords',
          name: 'keywords',
          content: keywords,
        },
        {
          vmid: 'og:title',
          property: 'og:title',
          content: title,
        },
        {
          vmid: 'og:description',
          property: 'og:description',
          content: description,
        },
        {
          vmid: 'og:image',
          property: 'og:image',
          content: image,
        },
        {
          vmid: 'og:url',
          property: 'og:url',
          content: window.location.href,
        },
      ],
    };
  },
  computed: {
    ...mapState({
      language: (state) => state.language,
      loading: (state) => state.tickets.loadingList,
      loaded: (state) => state.tickets.loadedList,
      events: (state) => state.tickets.data.events,
      groups: (state) => state.tickets.data.groups,
      publicURL: (state) => state.app.API_PUBLIC_URL,
      venues: (state) => state.tickets.data.venues,
      eventOrder: (state) => state.tickets.data.eventOrder,
      meta: (state) => state.meta.data,
      navTranslations: (state) => state.app.navTranslations,
      language: (state) => state.language,
    }),
    filteredEventOrder() {
      const filteredOrder = [];
      this.eventOrder.forEach((eo) => {
        if (typeof this.filteredEvents[eo] !== 'undefined') {
          filteredOrder.push(eo);
        }
      });
      return filteredOrder;
    },
    ...mapGetters({
      filteredEvents: 'tickets/filteredEvents',
    }),
    hasMultipleVenues() {
      return Object.keys(this.$store.state.tickets.data.eventIdsPerVenue).length > 0;
    },
  },
  props: {
    isHome: { default: false },
  },
  methods: {
    ...mapActions({
      getList: 'tickets/getList',
      setVenueFilter: 'tickets/setVenueFilter',
      setGroupFilter: 'tickets/setGroupFilter',
    }),
    getNavName(type) {
      if (typeof this.navTranslations !== 'undefined') {
        if (typeof this.navTranslations[type] !== 'undefined') {
          if (
            typeof this.navTranslations[type][this.language] !== 'undefined' &&
            this.navTranslations[type][this.language] !== ''
          ) {
            return this.navTranslations[type][this.language];
          }
        }
      }
      return this.$t(type);
    },
    setVenueFilterLocal(p) {
      this.setVenueFilter(p);
      this.setUrlQuery();
    },
    setGroupFilterLocal(p) {
      this.setGroupFilter(p);
      this.setUrlQuery();
    },

    setUrlQuery() {
      const query = { venue: [], group: [] };
      Object.keys(this.venues).forEach((c) => {
        const venue = this.venues[c];
        if (venue.selected) {
          query.venue.push(venue.name);
        }
      });

      Object.keys(this.groups).forEach((l) => {
        const group = this.groups[l];
        if (group.selected) {
          query.group.push(group.name);
        }
      });

      this.$router.replace({ query });
    },
    parseQuery() {
      let queryVenues = [];
      let queryGroups = [];

      if (!Array.isArray(this.$route.query.venue)) {
        queryVenues.push(this.$route.query.venue);
      } else {
        queryVenues = this.$route.query.venue;
      }
      queryVenues.forEach((qc) => {
        const id = Object.keys(this.venues).find((c) => qc === this.venues[c].name);
        if (typeof id !== 'undefined') {
          this.setVenueFilter({ key: id, selected: true });
        }
      });

      if (!Array.isArray(this.$route.query.group)) {
        queryGroups.push(this.$route.query.group);
      } else {
        queryGroups = this.$route.query.group;
      }
      queryGroups.forEach((ql) => {
        const id = Object.keys(this.groups).find((l) => ql === this.groups[l].name);
        if (typeof id !== 'undefined') {
          this.setGroupFilter({ key: id, selected: true });
        }
      });
    },
    formatDate(val) {
      return formater.date(val);
    },
    formatTime(val) {
      return formater.time(val);
    },

    formatDateTime(val) {
      return formater.dateTime(val);
    },
    formatStartAndEndDates(s, e) {
      const start = moment(s);
      const end = moment(e);
      let startString = this.formatDateTime(start);
      let endString = this.formatDateTime(end);
      let separator = ' – ';

      const hasStarted = start.isBefore(moment());
      const isSameDay = start.isSame(end, 'day');
      const sevenDaysFromStart = start.clone();
      sevenDaysFromStart.add(7, 'days');
      const lastsMoreThan7Days = end.isAfter(sevenDaysFromStart, 'days');

      if (hasStarted && lastsMoreThan7Days) {
        startString = this.formatDate(start);
        endString = this.formatDate(end);
      } else if (!hasStarted) {
        startString = this.formatDate(start);
        endString = this.formatDate(end);
      }

      if (isSameDay) {
        startString = this.formatDateTime(start);
        separator = '';
        endString = '';
      }
      if (end.isAfter(moment().add(6, 'months'), 'months')) {
        endString = '';
      }
      if (endString === '') {
        separator = '';
        if (start.isBefore(moment().subtract(6, 'months'), ' months')) {
          startString = '';
        }
      }
      return startString + separator + endString;
    },
    imageLoaded(e) {
      if (typeof e.classList !== 'undefined') {
        e.classList.add('loaded');
      }
    },
  },
  watch: {
    events: function () {
      this.parseQuery();
    },
  },
  mounted() {
    this.showEvents = true;
    setPageTitle(`${this.getNavName('NAV_TICKETS')}`);
  },
};
</script>

<style lang="scss" scoped>
@import '../../scss/grid';
@import '../../scss/scaffolding'; // img-responsive
@import '../../scss/mixins';

.eventsPage {
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
  @include blockPadding;
}

.events {
  @extend .row;
  clear: both;
  width: 100%;
  > .eventContainer {
    display: flex;
    flex-wrap: wrap;
  }
}
/*
.venues {
  > span {
    display: flex;
    flex-wrap: wrap;
  }

}
*/
.event {
  @extend .col-lg-3;
  @extend .col-md-4;
  @extend .col-sm-6;
  display: inline-block;
  float: none;
  vertical-align: top;
  margin-bottom: $sp * 3;
  img {
    @extend .img-responsive;
  }
  .title {
    text-decoration: underline;
    line-height: 20px;
    margin-top: 8px;
  }
  .calendar {
    margin-top: calc($sp/2);
    margin-bottom: calc($sp/2);
  }
  .venue {
    padding-top: calc($sp/2);
    font-weight: 600;
  }
  .linkBlock {
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    .imageContainer {
      margin-bottom: $sp;
    }
  }
}

.noItems {
  text-align: center;
  padding-top: 5 * $sp;
  padding-left: $sp;
  padding-right: $sp;
  color: $brand-lighter;
}
.linkBlock,
.linkBlock:hover,
.linkBlock:active {
  color: $text-color;
  text-decoration: none;
}

.filters {
  width: 100%;
  overflow: auto;
  display: flex;
  justify-content: space-between;
  gap: $sp;
}

.venues,
.groups {
  margin-bottom: 4 * $sp;
  @media screen and (max-width: $screen-sm) {
    text-align: center !important;
  }
}
.venue,
.group {
  margin-bottom: calc($sp/3);
  margin-left: calc($sp/6);
  margin-right: calc($sp/6);
  display: inline-block;
}

img {
  opacity: 0;
  transition: opacity 0.5s;
}
img.loaded {
  opacity: 1;
}

.fade-enter-active,
.fade-leave-active {
  transition: all 1s;
}
.fade-enter, .fade-leave-to /* .list-leave-active below version 2.1.8 */ {
  opacity: 0;
}

.textContainer {
  min-height: 9em;
}
.venueName {
  vertical-align: top;
}
</style>
